import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const DOMAIN_SOURCE =
  'https://chromium.googlesource.com/chromium/src/+/refs/heads/main/docs/security/document-domain.md';
const DOMAIN_BLOG = 'https://developer.chrome.com/blog/document-domain-setter-deprecation/';
const SITE_ISOLATION = 'https://developer.chrome.com/blog/site-isolation/';

/**
 * Spectre-mitigation policy signals (security-research §7).
 *
 * Two cheap, genuinely observable behaviours:
 *
 *  1. document.domain — Chrome froze it, then restricted it to public
 *     suffixes, as a Spectre-adjacent hardening measure on a staggered rollout.
 *     Whether the relaxation still works (and whether it is silently ignored vs
 *     throwing) is a clean version-boundary fingerprint. A no-op is NOT always
 *     policy: it also reflects Origin-Agent-Cluster, so we report the reading
 *     rather than declaring a version.
 *
 *  2. Site Isolation timing — Chrome puts cross-site frames in separate OS
 *     processes, so a cross-site postMessage round trip is measurably slower
 *     than a same-origin one. Mobile Chrome and low-memory configurations
 *     weaken or disable full isolation, making the ratio a device-class signal.
 *
 *     The baseline is a SAME-ORIGIN IFRAME (srcdoc), and the comparison is a
 *     CROSS-SITE IFRAME whose URL comes from VITE_CROSS_SITE_FRAME_URL. Note
 *     that a srcdoc frame inherits the parent's origin — it is same-origin, NOT
 *     an opaque origin — so an earlier revision's "opaque frame" label was
 *     wrong; it was measuring same-origin to same-origin. Two Cloudflare Pages
 *     projects are usable as the two sites because `pages.dev` is on the public
 *     suffix list, so `a.pages.dev` and `b.pages.dev` are cross-site. (A
 *     preview subdomain of the SAME project, e.g. `hash.a.pages.dev`, is
 *     cross-origin but same-site, so it is not a substitute — Site Isolation
 *     keys on the registrable domain.) The cross-site page must simply echo
 *     postMessage back to its parent; any message is accepted, checked by
 *     `event.source`. `scripts/echo-frame/` is a reference implementation.
 *
 *     When VITE_CROSS_SITE_FRAME_URL is unset there is no second site, so the
 *     probe reports "not exercised": no ratio, no verdict, and nothing in the
 *     fingerprint part. Both signals are legitimate configurations on real
 *     browsers, and a spoofed/mobile/low-memory result is not automation
 *     evidence, so nothing here is ever scored.
 */
export const securityHardeningProbe: Probe = {
  id: 'securityHardening',
  label: 'Security hardening policy',
  layer: 'engine',
  tier: 'client',
  category: 'browser',
  description: 'document.domain relaxation policy and same-origin vs cross-site frame round-trip ratio',
  sources: [DOMAIN_SOURCE, DOMAIN_BLOG, SITE_ISOLATION],
  budgetMs: 6000,

  async run(): Promise<ProbeResult> {
    // --- 1. document.domain relaxation ------------------------------------
    //
    // NOTE: on a modern Chromium this probe makes the console print
    //   "document.domain mutation is ignored because the surrounding agent
    //    cluster is origin-keyed."
    // That warning is CORRECT and is exactly what we are measuring: an
    // origin-keyed agent cluster (the default since Chrome 88) makes the setter
    // a no-op. We deliberately do not suppress it — it is informative output,
    // not noise, and it is the mechanism behind the `public-suffix-only`
    // (really "origin-keyed") policy bucket this probe reports.
    let domainSetterWorks = false;
    let domainSetterThrew = false;
    let domainSetterIgnored = false;
    const original = document.domain;
    try {
      // Setting the same value is a no-op; setting a *subdomain* of the current
      // host is what the relaxation used to allow. On a single-label host
      // (localhost / 127.0.0.1) there is no parent to relax to, so we detect
      // "ignores" rather than pretending to test the cross-origin case.
      const parts = location.hostname.split('.');
      const parent = parts.length > 1 ? parts.slice(1).join('.') : null;
      if (parent) {
        document.domain = parent;
        domainSetterWorks = document.domain === parent;
        domainSetterIgnored = document.domain === original;
      } else {
        // No parent domain to test against; the setter is a genuine no-op.
        domainSetterIgnored = true;
      }
    } catch {
      domainSetterThrew = true;
    } finally {
      try {
        document.domain = original;
      } catch {
        /* restoring is best-effort */
      }
    }

    const domainPolicy = domainSetterThrew
      ? 'frozen'
      : domainSetterWorks
        ? 'relaxable'
        : 'public-suffix-only';

    // --- 2. Site Isolation timing ------------------------------------------
    // Same-origin baseline: a srcdoc iframe (which inherits our origin).
    const sameOrigin = await measureFrameRoundTrip(openSameOriginFrame, 7);

    // Cross-site comparison: only possible with a real second site.
    const crossSiteUrl = (import.meta.env?.VITE_CROSS_SITE_FRAME_URL as string | undefined) ?? null;
    const exercised = crossSiteUrl !== null && crossSiteUrl !== '';
    const crossSite = exercised
      ? await measureFrameRoundTrip(() => openFrame({ src: crossSiteUrl }), 7)
      : null;

    // The ratio is only produced when BOTH frames round-tripped. Unmeasured
    // values are reported as na and never enter parts.
    const isolationRatio =
      crossSite != null && sameOrigin != null && sameOrigin > 0
        ? sig(crossSite / sameOrigin, 2)
        : null;

    const signals: ProbeResult['signals'] = {
      // document.domain policy
      documentDomainRelaxationWorks: ok(domainSetterWorks, 'client'),
      documentDomainSetterThrew: ok(domainSetterThrew, 'client'),
      documentDomainIgnored: ok(domainSetterIgnored, 'client'),
      // Policy bucket: relaxed (old), public-suffix-only (Chromium hardening),
      // or immutable (frozen). Reported, not scored.
      documentDomainPolicy: ok(domainPolicy, 'derived'),
      // Site Isolation measurements.
      sameOriginRoundTripUs: ok(sameOrigin == null ? null : sig(sameOrigin * 1000, 2), 'client'),
      siteIsolationExercised: ok(exercised, 'derived'),
      crossSiteFrameUrlConfigured: ok(crossSiteUrl ?? null, 'client'),
      crossOriginRoundTripUs:
        crossSite == null
          ? na<number>(
              'unavailable',
              'derived',
              exercised
                ? 'cross-site frame did not round-trip before the timeout (blocked, offline, or the page does not echo postMessage)'
                : 'set VITE_CROSS_SITE_FRAME_URL to a second-site page that echoes postMessage to its parent',
            )
          : ok(sig(crossSite * 1000, 2), 'client'),
      // >1.5x suggests a process boundary; ~1.0x suggests same-process
      // (no Site Isolation, or the frame was not genuinely cross-site).
      isolationRatio:
        isolationRatio == null
          ? na<number>(
              'unavailable',
              'derived',
              exercised ? 'needs both frame round trips' : 'cross-site frame URL not configured',
            )
          : ok(isolationRatio, 'derived'),
      siteIsolationApparent:
        isolationRatio == null
          ? na<boolean>('unavailable', 'derived', 'isolation ratio not measured')
          : ok(isolationRatio > 1.5, 'derived'),
      // Coarse bucket of the same value, for readability.
      isolationBucket:
        isolationRatio == null
          ? na<string>('unavailable', 'derived', 'isolation ratio not measured')
          : ok(
              isolationRatio > 2
                ? 'cross-process'
                : isolationRatio > 1.5
                  ? 'likely-cross-process'
                  : 'same-process',
              'derived',
            ),
    };

    return {
      signals,
      // No findings here: both signals are legitimate configurations on real
      // browsers, and a spoofed/mobile/low-memory result is not automation
      // evidence. Reported, never scored.
      //
      // ONLY documentDomainPolicy is hashed. The isolation ratio is a
      // ratio of two sub-millisecond postMessage round trips, and where the
      // page is not cross-origin isolated the clock is clamped to 100 us, so
      // the measured ratio is quantised noise that flips buckets between runs
      // in the SAME browser (the stability e2e proves it). Hashing it would
      // break the compare view for no benefit, so it stays a signal.
      parts: {
        engineTiming: {
          documentDomainPolicy: domainPolicy,
        },
      },
    };
  },
};

/** srcdoc inherits the parent origin, so this is a same-origin frame. */
function openSameOriginFrame(): Promise<OpenFrame | null> {
  return openFrame({
    srcdoc:
      '<!doctype html><script>' +
      'window.addEventListener("message", function () {' +
      '  parent.postMessage({ __fpl: "same-origin-ack" }, "*");' +
      '});' +
      '</' + 'script>',
  });
}

interface OpenFrame {
  ping(timeoutMs: number): Promise<number | null>;
  close(): void;
}

/**
 * Open ONE frame and resolve once it has loaded (null on timeout or setup
 * failure). onload also fires for a blocked/error document, so callers must
 * still treat a silent ping as "not measurable".
 */
function openFrame(opts: { src?: string; srcdoc?: string }): Promise<OpenFrame | null> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.width = '1px';
    iframe.style.height = '1px';

    let settled = false;
    const finish = (v: OpenFrame | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(loadTimer);
      if (v === null) iframe.remove();
      resolve(v);
    };

    iframe.onload = () => {
      finish({
        ping: (timeoutMs) => pingFrameWindow(iframe, timeoutMs),
        close: () => iframe.remove(),
      });
    };
    if (opts.srcdoc != null) iframe.srcdoc = opts.srcdoc;
    else iframe.src = opts.src!;
    const loadTimer = setTimeout(() => finish(null), 1500);
    document.body.appendChild(iframe);
  });
}

/** One postMessage round trip to an already-loaded frame, timeout-bounded. */
function pingFrameWindow(iframe: HTMLIFrameElement, timeoutMs: number): Promise<number | null> {
  return new Promise((resolve) => {
    let done = false;
    const t0 = performance.now();
    const onMsg = (e: MessageEvent) => {
      if (done || e.source !== iframe.contentWindow) return;
      done = true;
      window.removeEventListener('message', onMsg);
      clearTimeout(timer);
      resolve(performance.now() - t0);
    };
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      window.removeEventListener('message', onMsg);
      resolve(null);
    }, timeoutMs);
    window.addEventListener('message', onMsg);
    iframe.contentWindow?.postMessage({ __fpl: 'round-trip' }, '*');
  });
}

/**
 * Measure postMessage round trips on ONE warmed-up frame.
 *
 * Both the same-origin baseline and the cross-site comparison use this, so the
 * two numbers are collected identically. Opening a fresh cross-site iframe per
 * sample would mean one full document load (and a process hop) per sample —
 * slow, and unrelated to the property under test: the observable is postMessage
 * IPC latency across a site boundary, not page load. A single frame with one
 * untimed warm-up ping keeps the probe well inside its budget. Every step is
 * timeout-bounded, so a blocked or non-echoing frame degrades to null
 * (reported as unmeasured) rather than hanging the probe.
 */
async function measureFrameRoundTrip(
  open: () => Promise<OpenFrame | null>,
  samples = 7,
): Promise<number | null> {
  const frame = await open();
  if (!frame) return null;
  try {
    // Warm-up ping, not recorded: establishes the IPC channel.
    const warm = await frame.ping(600);
    if (warm == null) return null;
    const vals: number[] = [];
    const deadline = performance.now() + 2000;
    while (vals.length < samples && performance.now() < deadline) {
      const v = await frame.ping(800);
      if (v != null) vals.push(v);
    }
    if (!vals.length) return null;
    vals.sort((a, b) => a - b);
    return vals[Math.floor(vals.length / 2)];
  } finally {
    frame.close();
  }
}

registerProbe(securityHardeningProbe);
